"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { signIn, useSession } from "next-auth/react";
import React, { Suspense, useEffect, useRef, useState } from "react";
import { Components } from "@/app/(components)/_components/Components";
import PageLoader from "@/app/(components)/generics/loaders/PageLoader/PageLoader";
import { ModalType } from "@/app/(components)/generics/Modal/Modal";
import JobWatcher from "@/app/(components)/specifics/jobs/JobWatcher";
import Footer from "@/app/(components)/specifics/navigation/Footer/Footer";
import Header from "@/app/(components)/specifics/navigation/Header/Header";
import Sidebar from "@/app/(components)/specifics/navigation/Sidebar/Sidebar";
import { useAuthUser } from "@/app/(components)/specifics/wrappers/AuthProvider/AuthProvider";
import { useBrand } from "@/app/(components)/specifics/wrappers/BrandProvider/BrandProvider";
import { createProject } from "@/app/actions/project/create/action";
import { verifyEmail } from "@/app/actions/user/verify-email/action";
import { useFetchUser } from "@/app/hooks/fetchs/user/useFetchUser";
import { useProjectDocumentTitle } from "@/app/hooks/useProjectDocumentTitle";
import { initCrispChat } from "@/app/libs/CrispChat";
import { ModalStore } from "@/app/stores/ModalStore";
import { ProjectStore } from "@/app/stores/ProjectStore";
import { useSidebarOpen } from "@/app/stores/SidebarStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import { UStyles } from "@/app/utils/UStyles";
import { COMPANY_BASE_DOMAINS } from "@/app/vars/Company";
import { CookieKey } from "@/app/vars/Storage";
import { UrlAction, UrlParam } from "@/app/vars/Urls";
import styles from "./AppWrapper.module.css";

type AppWrapperProps = DefaultProps & {
  children?: React.ReactNode;
  initialSidebarOpen: boolean;
};

type ActionProcessorProps = DefaultProps & {
  status: string;
  onProcessing: (v: boolean) => void;
};

function ActionProcessor(props: ActionProcessorProps) {
  const T = useT();
  const { params, status, onProcessing } = props;

  const searchParams = useSearchParams();
  const router = useRouter();

  useEffect(() => {
    const authError = searchParams.get(UrlParam.ERROR);
    const authCallbackUrl = searchParams.get(UrlParam.CALLBACK_URL);
    if (authError && authCallbackUrl) {
      try {
        const target = new URL(authCallbackUrl);
        const trusted = COMPANY_BASE_DOMAINS.some((d) => target.hostname.endsWith(d));
        if (trusted && target.origin !== window.location.origin) {
          target.searchParams.set(UrlParam.ERROR, authError);
          window.location.replace(target.toString());
          return;
        }
      } catch (_e) {}
    }

    const e = searchParams.get(UrlParam.EMAIL);
    const k = searchParams.get(UrlParam.KEY);
    const action = searchParams.get(UrlParam.ACTION);

    const r = searchParams.get(UrlParam.REFERRAL_CODE);
    if (r) {
      document.cookie = `${CookieKey.REFERRAL_CODE}=${r}; path=/; max-age=2592000`;
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete(UrlParam.REFERRAL_CODE);
      window.history.replaceState({}, "", newUrl.toString());
    }

    if (e && k && action && status !== "loading") {
      const secretKey = k.replace(/ /g, "+");
      window.history.replaceState({}, "", window.location.pathname);

      if (action === UrlAction.RESET_PASSWORD) {
        ModalStore.getState().openModal(
          Components.ResetPassword,
          T(TKey.RESET_MY_PASSWORD),
          { params, email: e, secretKey },
          ModalType.SMALL,
        );
      } else if (action === UrlAction.CONFIRM_EMAIL) {
        onProcessing(true);
        verifyEmail(e, secretKey)
          .then(async (result) => {
            if (
              result.valid &&
              result.type === UrlAction.CONFIRM_EMAIL &&
              result.loginToken &&
              result.email
            ) {
              await signIn("credentials", {
                loginToken: result.loginToken,
                email: result.email,
                redirect: false,
              });
              router.refresh();
            }
          })
          .finally(() => {
            onProcessing(false);
          });
      } else if (action === UrlAction.CHANGE_EMAIL) {
        ModalStore.getState().openModal(
          Components.ChangeEmail,
          T(TKey.SETTINGS_CONFIRM_EMAIL_CHANGE),
          { params, newEmail: e, secretKey },
          ModalType.SMALL,
        );
      }
    }

    const openModal = searchParams.get(UrlParam.OPEN_MODAL);
    if (openModal === Components.Billing && status === "authenticated") {
      window.history.replaceState({}, "", window.location.pathname);
      ModalStore.getState().openModal(
        Components.Billing,
        T(TKey.CREDITS_BUY),
        { params },
        ModalType.MEDIUM,
      );
    }

    if (openModal === Components.SignUp && status === "unauthenticated") {
      const inviteEmail = searchParams.get(UrlParam.EMAIL);
      const inviteToken = searchParams.get(UrlParam.INVITE_TOKEN);
      window.history.replaceState({}, "", window.location.pathname);
      ModalStore.getState().openSignUp(
        params,
        null,
        inviteEmail ?? undefined,
        inviteToken ?? undefined,
      );
    }
  }, [searchParams, status, params, onProcessing, router.refresh, T]);

  return null;
}

export default function AppWrapper(props: AppWrapperProps) {
  const { children, params } = props;

  const isOpen = useSidebarOpen(props.initialSidebarOpen);

  const { status } = useSession();
  const { isLoading: isUserLoading } = useFetchUser({ enabled: status === "authenticated" });
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const projectsFetched = ProjectStore((state) => state.projectsFetched);
  const fetchUserProjects = ProjectStore((state) => state.fetchUserProjects);
  const hasProject = ProjectStore((state) => !!state.currentProject || state.projects.length > 0);
  const creatingProjectRef = useRef(false);

  const user = useAuthUser();
  const isLogged = !!user;
  const tenant = useBrand();
  const isBareTenantLogin = !!tenant && !isLogged;

  useProjectDocumentTitle(params.lang);

  useEffect(() => {
    if (tenant) {
      return;
    }
    initCrispChat();
  }, [tenant]);

  useEffect(() => {
    if (isLogged && !projectsFetched) {
      fetchUserProjects();
    }
  }, [isLogged, projectsFetched, fetchUserProjects]);

  useEffect(() => {
    if (!isLogged || !projectsFetched || hasProject || creatingProjectRef.current) {
      return;
    }

    creatingProjectRef.current = true;
    setIsCreatingProject(true);
    createProject()
      .then((created) => ProjectStore.getState().addProject(created))
      .catch(() => {})
      .finally(() => setIsCreatingProject(false));
  }, [isLogged, projectsFetched, hasProject]);

  const isBooting =
    status === "loading" ||
    (status === "authenticated" && isUserLoading && !projectsFetched) ||
    (isLogged && !projectsFetched) ||
    isCreatingProject ||
    isProcessingAction;

  return (
    <>
      <main className={UStyles.classNames(styles.container, isLogged ? styles.logged : "")}>
        <Suspense>
          <ActionProcessor params={params} status={status} onProcessing={setIsProcessingAction} />
        </Suspense>

        {isBooting && <PageLoader delay={300} />}

        {!isBooting && isLogged && <JobWatcher params={params} />}

        {!isBooting && isLogged && (
          <Sidebar params={params} isLogged={isLogged} initialOpen={props.initialSidebarOpen} />
        )}
        {!isBooting && !isBareTenantLogin && <Header params={params} />}

        <div
          hidden={isBooting}
          data-logged={isLogged ? "true" : "false"}
          className={UStyles.classNames(
            styles.content,
            isLogged ? styles.sidebarLayout : "",
            isLogged && !isOpen ? styles.sidebarIsClosed : "",
          )}
        >
          {children}
        </div>
      </main>

      {!isBooting && !isLogged && !isBareTenantLogin && <Footer params={params} />}
    </>
  );
}
